// Путь: D:\QR\QR-WEB\dashboard\src\components\User\MyVouchers.jsx
// Название: My Gifts - AI подарки пользователя
// Версия: 7.1.0
// Дата: 11.01.2026

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MyVouchers.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://qr-money.onrender.com';

const MyVouchers = () => {
  // ============================================
  // STATE
  // ============================================
  const [activeTab, setActiveTab] = useState('all');
  const [gifts, setGifts] = useState([]);
  const [hiddenItems, setHiddenItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const token = localStorage.getItem('authToken');

  // ============================================
  // ЗАГРУЗКА ДАННЫХ
  // ============================================
  useEffect(() => {
    fetchGifts();
  }, []);

  const fetchGifts = async () => {
    setLoading(true);
    setError('');
    try {
      // 0. Загружаем hidden items
      let hidden = [];
      try {
        const hiddenRes = await axios.get(`${API_URL}/api/profile/hidden-items`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (hiddenRes.data.success) {
          hidden = hiddenRes.data.data || [];
          setHiddenItems(hidden);
        }
      } catch (e) {}

      const isHidden = (type, id) => {
        return hidden.some(h => h.item_type === type && String(h.item_id) === String(id));
      };

      // 1. Brand подарки (полученные от брендов)
      const brandRes = await axios.get(`${API_URL}/api/brand-gifts/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // 2. B2B подарки (от кампаний)
      const b2bRes = await axios.get(`${API_URL}/api/gifts/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // 3. Созданные мной подарки (vouchers)
      const myRes = await axios.get(`${API_URL}/api/vouchers/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Преобразуем Brand подарки (с фильтрацией)
      const brandGifts = (brandRes.data.gifts || [])
        .filter(g => !isHidden('brand_gift', g.id))
        .map(g => ({
          ...g,
          source: 'received',
          type: 'brand_gift',
          amount: g.totalAmount || g.total_amount || g.spendAmount || 0,
          image: g.imageUrl || g.image_url,
          date: g.claimedAt || g.claimed_at,
          campaign: g.campaignName || null
        }));

      // Преобразуем B2B подарки (с фильтрацией)
      const b2bGifts = (b2bRes.data.gifts || [])
        .filter(g => !isHidden('b2b_gift', g.id))
        .map(g => ({
          ...g,
          source: 'received',
          type: 'b2b_gift',
          amount: g.amount,
          image: g.image_url,
          date: g.claimed_at,
          campaign: g.campaign_name || null,
          shortCode: g.short_code
        }));

      // Преобразуем созданные подарки (с фильтрацией)
      const myGifts = (myRes.data.data || [])
        .filter(v => !isHidden('voucher', v.id))
        .map(v => ({
          ...v,
          source: 'created',
          type: 'voucher',
          amount: v.amount,
          image: v.image_url,
          date: v.created_at,
          campaign: null
        }));

      setGifts([...brandGifts, ...b2bGifts, ...myGifts]);
    } catch (err) {
      console.error('Fetch error:', err);
      setError('Ошибка загрузки');
    } finally {
      setLoading(false);
    }
  };

  // ============================================
  // ФИЛЬТРАЦИЯ
  // ============================================
  const getFilteredGifts = () => {
    if (activeTab === 'received') return gifts.filter(g => g.source === 'received');
    if (activeTab === 'created') return gifts.filter(g => g.source === 'created');
    return gifts;
  };

  const filteredGifts = getFilteredGifts();

  // Статистика
  const stats = {
    all: gifts.length,
    received: gifts.filter(g => g.source === 'received').length,
    created: gifts.filter(g => g.source === 'created').length
  };

  // Сумма полученных
  const totalReceived = gifts
    .filter(g => g.source === 'received')
    .reduce((sum, g) => sum + parseFloat(g.amount || 0), 0);

  // ============================================
  // ДЕЙСТВИЯ
  // ============================================
  const downloadImage = (gift) => {
    if (!gift.image) return;
    const link = document.createElement('a');
    link.href = gift.image;
    link.download = 'DARY_Gift.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  const shareGift = async (gift) => {
    const text = '🎁 Тебе подарок! Открой: dary.one';
    if (navigator.share && gift.image) {
      try {
        await navigator.share({ title: 'DARY Gift', text, url: gift.image });
      } catch (e) {
        navigator.clipboard.writeText(text);
      }
    } else {
      navigator.clipboard.writeText(text);
    }
  };
  
  const handleDelete = async (gift) => {
    if (!window.confirm('Удалить подарок и вернуть средства на баланс?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/vouchers/${gift.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Обновляем список
      setGifts(prev => prev.filter(g => !(g.type === gift.type && g.id === gift.id)));
      fetchGifts();
    } catch (err) {
      console.error('Delete error:', err);
      alert(err.response?.data?.error || 'Ошибка удаления');
    }
  };
  
  // ============================================
  // ФОРМАТИРОВАНИЕ
  // ============================================
  const formatAmount = (amount) => parseFloat(amount || 0).toFixed(2);
  
  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('ru-RU', {
      day: '2-digit', month: '2-digit', year: '2-digit'
    });
  };

  const getStatusBadge = (gift) => {
    if (gift.source === 'received') {
      return <span className="mv-badge mv-badge-green">Получен</span>;
    }
    if (gift.status === 'active') {
      return <span className="mv-badge mv-badge-blue">Активен</span>;
    }
    if (gift.status === 'used') {
      return <span className="mv-badge mv-badge-gray">Использован</span>;
    }
    if (gift.status === 'cancelled') {
      return <span className="mv-badge mv-badge-red">Отменён</span>;
    }
    return <span className="mv-badge">{gift.status}</span>;
  };

  // ============================================
  // RENDER
  // ============================================
  if (loading) {
    return (
      <div className="mv-loading">
        <div className="mv-spinner"></div>
        <p>Загрузка подарков...</p>
      </div>
    );
  }

  return (
    <div className="mv-container">
      {/* Заголовок */}
      <div className="mv-header">
        <h1>🎁 Мои подарки</h1>
        <p>История полученных и созданных AI подарков</p>
      </div>

      {/* Табы */}
      <div className="mv-tabs">
        <button 
          className={`mv-tab ${activeTab === 'all' ? 'active' : ''}`}
          onClick={() => setActiveTab('all')}
        >
          Все <span className="mv-tab-count">{stats.all}</span>
        </button>
        <button 
          className={`mv-tab ${activeTab === 'received' ? 'active' : ''}`}
          onClick={() => setActiveTab('received')}
        >
          Полученные <span className="mv-tab-count">{stats.received}</span>
        </button>
        <button 
          className={`mv-tab ${activeTab === 'created' ? 'active' : ''}`}
          onClick={() => setActiveTab('created')}
        >
          Созданные <span className="mv-tab-count">{stats.created}</span>
        </button>
      </div>

      {/* Баннер общей суммы */}
      {totalReceived > 0 && (
        <div className="mv-total-banner">
          💎 Получено на сумму: <strong>{totalReceived.toFixed(2)} DAR</strong>
        </div>
      )}

      {/* Ошибка */}
      {error && <div className="mv-error">{error}</div>}

      {/* Пустое состояние */}
      {filteredGifts.length === 0 ? (
        <div className="mv-empty">
          <div className="mv-empty-icon">🎨</div>
          <h3>Пока нет подарков</h3>
          <p>Создайте свой первый AI подарок или получите от друзей</p>
        </div>
      ) : (
        /* Сетка подарков */
        <div className="mv-grid">
          {filteredGifts.map((gift, index) => (
            <div key={`${gift.type}-${gift.id || index}`} className="mv-card">
              {/* Картинка */}
              <div className="mv-card-image">
                {gift.image ? (
                  <img src={gift.image} alt="Gift" />
                ) : (
                  <div className="mv-card-placeholder">🎁</div>
                )}
                {/* Бейдж источника */}
                <div className={`mv-source-badge ${gift.source}`}>
                  {gift.source === 'received' ? '📥' : '📤'}
                </div>
              </div>

              {/* Информация */}
              <div className="mv-card-info">
                <div className="mv-card-amount">{formatAmount(gift.amount)} DAR</div>
                {getStatusBadge(gift)}
                {gift.campaign && gift.campaign.length > 3 && (
                  <div className="mv-card-campaign">{gift.campaign}</div>
                )}
                <div className="mv-card-date">{formatDate(gift.date)}</div>
              </div>

              {/* Действия */}
              <div className="mv-card-actions">
                {gift.image && (
                  <button onClick={() => downloadImage(gift)} className="mv-action-btn" title="Скачать">
                    ⬇️
                  </button>
                )}
                {gift.image && (
                  <button onClick={() => shareGift(gift)} className="mv-action-btn" title="Поделиться">
                    📤
                  </button>
                )}
                <button onClick={() => handleDelete(gift)} className="mv-action-btn mv-action-delete" title="Удалить">
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyVouchers;